// SCR-STAFF-997 — پروفایل عمومی من (فقط پیش‌نویس با chn.provider_profile.manage_own).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, LoadingState, PageHeader } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

export const SCR_STAFF_997 = 'SCR-STAFF-997';

export function MyProviderProfilePage() {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('chn.provider_profile.manage_own');
  const queryClient = useQueryClient();
  const [bio, setBio] = useState('');
  const [slug, setSlug] = useState('');

  const list = useQuery({
    queryKey: ['chn', session?.tenantId, 'my-provider-profile'],
    queryFn: async () => {
      const body = await unwrap(tenantApi.GET('/api/v1/channels/public-page/providers'));
      return (body as { items: { providerId: string; displayName: string; draft?: { slug?: string | null }; slug?: string | null; bio?: string | null }[] }).items;
    },
  });

  const mine = list.data?.[0];
  useEffect(() => {
    if (mine) {
      setBio(mine.bio ?? '');
      setSlug(mine.draft?.slug ?? mine.slug ?? '');
    }
  }, [mine]);

  const save = useMutation({
    mutationFn: async () => {
      if (!mine) {
        throw new Error('provider');
      }
      return unwrap(
        tenantApi.PUT('/api/v1/channels/public-page/providers/{providerId}/draft' as never, {
          params: { path: { providerId: mine.providerId } },
          body: {
            content: {
              education: [],
              experience: [],
              awards: [],
              socialLinks: [],
              acceptedInsurerCodes: [],
              acceptedContractIds: [],
              mapUrl: null,
              showRemainingCapacity: false,
              slug: slug.trim() || null,
            },
          },
        } as never),
      );
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['chn', session?.tenantId, 'my-provider-profile'] }),
  });

  if (list.error) {
    return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  }
  if (!list.data) {
    return <LoadingState rows={3} />;
  }

  return (
    <Stack spacing={2} data-screen-id={SCR_STAFF_997} data-testid="scr-staff-997">
      <PageHeader titleKey="chn:presence.my_title" subtitleKey="chn:presence.my_subtitle" />
      {!write ? <Alert severity="warning">{t('chn:presence.manage_own_required')}</Alert> : null}
      {!mine ? <Alert severity="info">{t('chn:presence.empty')}</Alert> : null}
      {mine ? (
        <>
          <Alert severity="info">{t('chn:presence.draft_only_note')}</Alert>
          <TextField label={t('chn:presence.slug')} value={slug} onChange={(event) => setSlug(event.target.value)} disabled={!write} fullWidth />
          <TextField label={t('chn:channels.providers.bio')} value={bio} onChange={(event) => setBio(event.target.value)} disabled multiline minRows={3} fullWidth />
          <Button variant="contained" disabled={!write || save.isPending} onClick={() => save.mutate()} data-testid="my-profile-save-draft">
            {t('chn:presence.save_draft')}
          </Button>
        </>
      ) : null}
    </Stack>
  );
}
